import AddCircleOutlined from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveCircleOutlined from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { css, styled, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { IncrementalChange } from '@openx/types/auditTrail';
import type { LabeledChange } from '@openx/utils/history/types';
import isEmpty from 'lodash/isEmpty';
import type { JSX } from 'react';
import { ValuePlaceholder } from '../Value/ValuePlaceholder/ValuePlaceholder';

const StyledHistoryChangeSpan = styled('span')`
  display: block;
  white-space: nowrap;
`;

const sharedIconStyles = (theme: Theme) => css`
  font-size: inherit;
  line-height: inherit;
  margin-right: ${theme.spacing(0.5)};
  margin-top: ${theme.spacing(1)};
`;

const StyledAddIcon = styled(AddCircleOutlined)`
  color: ${({ theme }) => theme.palette.success.main};

  ${({ theme }) => sharedIconStyles(theme)}
`;

const StyledRemoveIcon = styled(RemoveCircleOutlined)`
  color: ${({ theme }) => theme.palette.error.main};

  ${({ theme }) => sharedIconStyles(theme)}
`;

const StyledContainerDiv = styled('div')`
  display: flex;
`;

interface SingleFieldIncrementalChangeProps {
  change: LabeledChange<IncrementalChange>;
}

export function SingleFieldIncrementalChange({ change }: SingleFieldIncrementalChangeProps): JSX.Element {
  const { added, removed } = change;

  if (isEmpty(added) && isEmpty(removed)) {
    return <ValuePlaceholder />;
  }

  const addIcon = <StyledAddIcon data-test="add-icon" />;
  const removeIcon = <StyledRemoveIcon data-test="remove-icon" />;

  const renderOperationChanges = (icon: JSX.Element, operationChanges?: string[]): JSX.Element | null => {
    if (!operationChanges || operationChanges.length === 0) {
      return null;
    }

    return (
      <StyledContainerDiv data-test="operation">
        {icon}
        <Typography variant="body1">
          {operationChanges.map((item, index) => (
            <span key={index}>
              <StyledHistoryChangeSpan>{item}</StyledHistoryChangeSpan>
            </span>
          ))}
        </Typography>
      </StyledContainerDiv>
    );
  };

  return (
    <>
      {renderOperationChanges(addIcon, added)}
      {renderOperationChanges(removeIcon, removed)}
    </>
  );
}
